import { useId, useState, type ReactNode } from "react";
import { Icon, cn } from "@nexus/ui";

export interface CollapsibleSectionProps {
  /** Section heading (also the button's accessible name). */
  title: string;
  /** Optional count badge next to the title (e.g. number of comments/segments). */
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Per-step disclosure used on the script-review report so the team can QA each
 * pipeline step individually. The heading doubles as the toggle; the body is
 * animated open/closed via the grid-rows trick (content stays mounted, so it
 * remains accessible and searchable). Collapsed by default.
 *
 * Mirrors `features/preflight/components/CollapsibleSection` (kept feature-local
 * — `@nexus/ui` exports no shared Accordion and the other copies are scoped to
 * their own features).
 */
export function CollapsibleSection({
  title,
  count,
  defaultOpen = false,
  children,
  className,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const headingId = useId();
  const bodyId = useId();

  return (
    <section className={cn("flex flex-col gap-2", className)} aria-labelledby={headingId}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="group focus-visible:ring-ring/40 -mx-1 flex w-full items-center justify-between gap-3 rounded-sm px-1 py-1 text-left outline-none focus-visible:ring-[3px]"
      >
        <span id={headingId} className="flex items-center gap-2 text-sm font-semibold">
          {title}
          {count != null && (
            <span className="text-muted-foreground rounded-full bg-muted px-2 py-0.5 text-xs font-normal">
              {count}
            </span>
          )}
        </span>
        <Icon
          name="chevron-down"
          size="sm"
          aria-hidden
          className={cn(
            "text-muted-foreground/60 group-hover:text-foreground shrink-0 transition-transform duration-200 motion-reduce:transition-none",
            open ? "" : "-rotate-90",
          )}
        />
      </button>
      <div
        id={bodyId}
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="pt-1">{children}</div>
        </div>
      </div>
    </section>
  );
}
